import React, {useEffect} from 'react'
import {observer} from 'mobx-react'
import {SubmitHandler, UseFormReturn} from 'react-hook-form'
import {Field} from '../../../components/field'
import {CheckIcon, CloseIcon} from '../../../components/icons'
import {formatPostcode, useCheckPostcodeFields} from '../../../hooks/forms/useCheckPostcodeFields'
import {useBasketStore} from '../../../store/hooks/useStore'
import {Box, Button, Flex, Stack, Text} from '../../../vanilla'
import * as styles from '../styles.css'
import {CheckPostalCodeFormFields} from '../../../types/forms'
import useDeliveryBooking from '../../../hooks/use-delivery-booking'
import SlotsErrorMessaging from './booking-slots/error-messaging'
import {useBaglessStores} from '../../../hooks/use-bagless-stores'
import {useGlobalStore} from '../../../store/hooks/useStore'


interface EditPostCodeProps {
  form: UseFormReturn<CheckPostalCodeFormFields>
  setPostalCode: (postcode: string) => void
  checkDeliveryValidity: ReturnType<typeof useDeliveryBooking>['checkDeliveryValidity']
  setPostCodeAvailability: ReturnType<typeof useDeliveryBooking>['setPostCodeAvailability']
  postCodeAvailability: ReturnType<typeof useDeliveryBooking>['postCodeAvailability']
}

export const EditPostCode = observer((props: EditPostCodeProps) => {
  const {
    form,
    setPostalCode,
    checkDeliveryValidity,
    postCodeAvailability,
    setPostCodeAvailability,
  } = props

  const {deliveryPostalCode, postcodeError, setPostcodeError} = useBasketStore()

  const fields = useCheckPostcodeFields({
    form,
    setPostCodeAvailability,
  })

  const {isBaglessStore} = useBaglessStores()
  const {selectedStoreId} = useGlobalStore()
  const showBaglessMessage = isBaglessStore(selectedStoreId ?? "")

  useEffect(() => {
    form.watch('postcode')
  }, [])

  useEffect(() => {
    if (deliveryPostalCode) {
      form.setValue('postcode', deliveryPostalCode)
    }
  }, [deliveryPostalCode])

  const onSubmit: SubmitHandler<CheckPostalCodeFormFields> = async ({postcode}) => {
    const formattedPostcode = formatPostcode(postcode)
    form.setValue('postcode', formattedPostcode)
    await checkDeliveryValidity(formattedPostcode)
    await setPostalCode(formattedPostcode)
  }

  if (postcodeError) {
    return <SlotsErrorMessaging variant='bookASlot' buttonAction={() => { setPostcodeError?.(false) }} />
  }
  return (
    <Stack>
      <Text variant="text4">
        Please enter your postcode below to check if we are able to deliver to your address.
      </Text>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack>
          <Stack spacing="4px">
            <Box className={styles.postalCodeInput}>
              <Field {...fields.postcode} />
            </Box>
            {postCodeAvailability && (
              <Box marginX="16px">
                {postCodeAvailability === 'available' ? (
                  <Flex align="center" gap="4px">
                    <CheckIcon color="accent1" width="16px" height="16px" />
                    <Text variant="text4">Online delivery available</Text>
                  </Flex>
                ) : postCodeAvailability === 'unavailable' ? (
                  <Box>
                    <Flex align="center" gap="4px">
                      <CloseIcon color="error" width="16px" height="16px" />
                      <Text variant="text4">Online delivery unavailable</Text>
                    </Flex>
                    <Text paddingY="8px" variant="text2" style={{color: '#bebebe'}}>
                      We don't deliver to your area yet. Continue to register your interest and
                      we'll be in touch if our service comes to your area.
                    </Text>
                  </Box>
                ) : null}

                { showBaglessMessage &&
                  <Flex align="center" gap="4px">
                    <CheckIcon color="accent1" width="16px" height="16px" />
                    <Text variant="text4">Your deliveries will be bagless</Text>
                  </Flex>
                }
              </Box>
            )}
          </Stack>
          <Box marginY="20px" marginX="16px">
            <Button
              size="sm"
              variant="secondary"
              type="submit"
            >
              Edit Postcode
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  )
})
